import { Range as IRange } from "datatypes/SoramaLocalApi";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { derivatives } from "libraries/Math/Derivatives";
import { movingAverage } from "libraries/Math/MovingAverage";
import { Frequency } from "units/Frequency";
import { Pressure } from "units/Pressure";
import { UnitSpectrum } from "units/UnitSpectrum";

export class PeakInfo {
    position: number;
    width: number;
    height: number;
}

export class FrequencyRangeSuggestionOptions {
    // Only peaks in given range of frequencies are considered
    minFrequency: number;
    maxFrequency: number;

    // Minimum peak distance (in Hz). Peaks that are too close to other (higher) peaks are filtered out
    minPeakDistance: number;

    // Minimum peak width (in Hz). Peaks that are not wide enough are filtered out
    widthThreshold: number;

    // The number of peaks that are considered (after filtering on peak distance, and before filtering on width) is limited
    maxNumberOfPeaks: number;

    // Only a limited number of suggested ranges are returned as the final result
    maxNumberOfOutputRanges: number;

    // Width (in Hz) of window used to smooth out the signal
    smoothingWindow: number;

    // Width (in Hz) around the peak that can be used to find the peak width
    peakWindow: number;
}

export class FrequencyRangeSuggestion {
    // Note: Default options are now handled by FrequencySuggestionService
    // to maintain a single source of truth for all frequency suggestion parameters

    // Generate a list of suggested frequency ranges for a given spectrum.
    // This algorithm looks for peaks in the spectrum, prioritizing peaks with a large width, and returns ranges for peaks that are likely to give good far field results
    public static computeSuggestedFrequencies(
        spectrum: UnitSpectrum,
        options: FrequencyRangeSuggestionOptions
    ): IRange[] {
        const peaks: PeakInfo[] = [];

        const pascalSpectrum = spectrum.convertToUnits(Frequency.hertz, Pressure.pascal);

        // Only consider peaks between min and max frequency bounds
        const limitedSpectrum = pascalSpectrum.data.filter(
            (p) => p.x >= options.minFrequency && p.x <= options.maxFrequency
        );

        // Double smoothing; higher resolution on x-axis compared to the research algorithm caused a bit more noise that needs filtering
        const smoothedSpectrum = movingAverage(
            movingAverage(limitedSpectrum, options.smoothingWindow),
            options.smoothingWindow
        );
        const spectrumDerivatives = derivatives(smoothedSpectrum);

        // Find peaks; Detect zero-crossing in derivative
        for (let i = 0; i < spectrumDerivatives.length - 1; i++) {
            if (Math.sign(spectrumDerivatives[i].y) > Math.sign(spectrumDerivatives[i + 1].y)) {
                const peakFrequency = smoothedSpectrum[i].x;

                // Find peak height and width from prominence. Uses the approach from SciPy, calculating the width at R=0 (the bottom of the peak)
                // https://docs.scipy.org/doc/scipy/reference/generated/scipy.signal.peak_prominences.html
                // https://docs.scipy.org/doc/scipy/reference/generated/scipy.signal.peak_widths.html

                // Find area of interest from left and right points, which are either
                // - Higher Y value than the peak (i.e. where the next peak has started)
                // - At the edge of the peak window
                // - At the edge of the limited spectrum range

                const peakWindowMinFrequency = smoothedSpectrum[i].x - options.peakWindow / 2;
                const peakWindowMaxFrequency = smoothedSpectrum[i].x + options.peakWindow / 2;

                const peakWindowMinIndex = Math.max(
                    0,
                    ArrayExtensions.fastFindClosestValueIndex(smoothedSpectrum, peakWindowMinFrequency, (p) => p.x)
                );
                const peakWindowMaxIndex = Math.min(
                    smoothedSpectrum.length - 1,
                    ArrayExtensions.fastFindClosestValueIndex(smoothedSpectrum, peakWindowMaxFrequency, (p) => p.x)
                );

                let leftPointIndex = -1;
                for (let l = 1; i - l > peakWindowMinIndex; l++) {
                    const element = smoothedSpectrum[i - l];

                    // Only accept values at least 1% above the peak; smoothing + derivative can lead to a 'peak' being in between two near-identical values
                    if (element.y > smoothedSpectrum[i].y * 1.01) {
                        leftPointIndex = i - l;
                        break;
                    }
                }
                // Left point not found inside peak window, just take the leftmost possible point
                if (leftPointIndex == -1) {
                    leftPointIndex = peakWindowMinIndex;
                }

                let rightPointIndex = -1;
                for (let r = 1; i + r < peakWindowMaxIndex; r++) {
                    const element = smoothedSpectrum[i + r];

                    // Only accept values at least 1% above the peak; smoothing + derivative can lead to a 'peak' being in between two near-identical values
                    if (element.y > smoothedSpectrum[i].y * 1.01) {
                        rightPointIndex = i + r;
                        break;
                    }
                }
                // Right point not found inside window, just take the rightmost possible point
                if (rightPointIndex == -1) {
                    rightPointIndex = peakWindowMaxIndex;
                }

                // Find the peak base, which is at a minimum in the area of interest, i.e. the minimum on the left or right side
                const leftArray = smoothedSpectrum.slice(leftPointIndex, i);
                let leftMinPoint = smoothedSpectrum[leftPointIndex];
                for (let l = 1; l < leftArray.length; ++l) {
                    if (leftArray[l].y < leftMinPoint.y) {
                        leftMinPoint = leftArray[l];
                    }
                }

                const rightArray = smoothedSpectrum.slice(i, rightPointIndex);
                let rightMinPoint = smoothedSpectrum[rightPointIndex];
                for (let r = rightArray.length - 1; r >= 0; --r) {
                    if (rightArray[r].y < rightMinPoint.y) {
                        rightMinPoint = rightArray[r];
                    }
                }

                // The height of the peak is the difference between the top of the peak and the _higher_ one of the two minima
                const peakBasePoint = leftMinPoint.y > rightMinPoint.y ? leftMinPoint : rightMinPoint;
                const peakBaseIsLeft = leftMinPoint.y > rightMinPoint.y;
                const peakHeight = smoothedSpectrum[i].y - peakBasePoint.y;

                if (peakHeight > 0) {
                    // The width of the peak is the width at the peak base,
                    // i.e. the horizontal distance between the base point and the point where the spectrum is at the same height on the other side
                    let peakWidth = 0;

                    if (peakBaseIsLeft) {
                        // Look for the first time that the spectrum falls below the baseline height on the right side of the peak
                        for (let r = 0; r < rightArray.length; r++) {
                            if (rightArray[r].y < peakBasePoint.y) {
                                peakWidth = rightArray[r].x - peakBasePoint.x;
                                break;
                            }
                        }
                    } else {
                        // Look for the first time that the spectrum falls below the baseline height on the left side of the peak
                        for (let l = leftArray.length - 1; l > 0; l--) {
                            if (leftArray[l].y < peakBasePoint.y) {
                                peakWidth = peakBasePoint.x - leftArray[l].x;
                                break;
                            }
                        }
                    }

                    // Filter out candidate peaks that are not wide enough
                    if (peakWidth > options.widthThreshold) {
                        peaks.push({
                            position: peakFrequency,
                            height: peakHeight,
                            width: peakWidth,
                        });
                    }
                }
            }
        }

        // Sort peaks by descending height
        const sortedPeaks = peaks.sort((p1, p2) => p2.height - p1.height).slice(0, options.maxNumberOfPeaks);

        // Filter out peaks that are too close together (prioritizing high peaks due to the sorting)
        const filteredPeaks: PeakInfo[] = [];

        sortedPeaks.forEach((p) => {
            if (!filteredPeaks.some((f) => Math.abs(f.position - p.position) < options.minPeakDistance)) {
                filteredPeaks.push(p);
            }
        });

        // Re-sort the peaks to prioritize by area
        filteredPeaks.sort((p1, p2) => p2.width * p2.height - p1.width * p1.height);

        // Return suggestion ranges for the widest peaks
        const resultRanges = filteredPeaks.slice(0, options.maxNumberOfOutputRanges).map((p) => {
            return {
                from: p.position - p.width / 2,
                to: p.position + p.width / 2,
            };
        });

        return resultRanges;
    }
}
